/**
 * As guias FIXADAS do acesso rápido (Gisele: "fixar as guias mais
 * utilizadas").
 *
 * Esclarecido pelo dono do produto em 03/11/2026: "pode ter um pin nela ou
 * algo ao passar o mouse e daí clica e fixa para aquele usuário".
 *
 * ## O que fixar RESOLVE
 *
 * A barra de abas não apaga nada sozinha, mas ela tem largura: o que não cabe
 * vai para o menu de excedente (`+N`). Com dez abas abertas, justamente as que
 * a pessoa mais usa somem dali conforme a ordem muda.
 *
 * Fixar é dizer "esta não desce". A guia fixada vem sempre primeiro, e por
 * isso nunca cai no `+N`.
 *
 * ## Por que no navegador, e não no servidor
 *
 * Porque é onde o resto do acesso rápido já mora: quais abas estão abertas
 * (`quickAccessPages`) e em que ordem (`quickAccessGlobalOrder`). Guardar só
 * a fixação no servidor criaria um estado meio migrado -- a fixação seguiria a
 * pessoa para outro computador e as abas ficariam para trás, o que é mais
 * confuso que as duas ficarem juntas.
 *
 * Se um dia o acesso rápido inteiro for para o servidor, isto vai junto.
 *
 * ## O formato é o MESMO da ordem
 *
 * `{ typeId, itemId }`, igual a `quickAccessGlobalOrder`. Uma aba é
 * identificada por esse par em todo o acesso rápido; inventar uma terceira
 * forma de nomear a mesma coisa seria o começo das duas divergirem.
 */

const CHAVE = 'quickAccessPinned';

/** A identidade de uma guia: o par tipo + item, como na ordem. */
const identidade = (typeId, itemId) => `${typeId}\u0000${itemId}`;

/**
 * As guias fixadas gravadas neste navegador.
 *
 * Devolve lista vazia para QUALQUER coisa que não seja uma lista de pares
 * válidos. O `localStorage` é escrito por versões anteriores da tela e pode
 * ser editado à mão: uma entrada torta aqui não pode derrubar a barra de abas
 * inteira no render.
 */
export const lerGuiasFixadas = () => {
    try {
        const cru = JSON.parse(localStorage.getItem(CHAVE) || '[]');

        if (!Array.isArray(cru)) return [];

        return cru.filter((e) => e && e.typeId !== undefined && e.itemId !== undefined);
    } catch (e) {
        return [];
    }
};

/**
 * Grava, e devolve o que gravou.
 *
 * Engolir a falha é deliberado: em aba anônima ou com o armazenamento cheio, o
 * `setItem` estoura -- e perder a fixação é muito melhor que derrubar a barra
 * de abas no meio de um atendimento.
 */
export const gravarGuiasFixadas = (fixadas) => {
    try {
        localStorage.setItem(CHAVE, JSON.stringify(fixadas));
    } catch (e) {
        // sem espaço ou sem permissão: a tela segue com o valor em memória.
    }

    return fixadas;
};

export const ehGuiaFixada = (fixadas, typeId, itemId) => (
    (Array.isArray(fixadas) ? fixadas : []).some(
        (e) => e && e.typeId === typeId && String(e.itemId) === String(itemId)
    )
);

/** Fixa o que não estava fixado, e desfixa o que estava. */
export const alternarGuiaFixada = (fixadas, typeId, itemId) => {
    const atuais = Array.isArray(fixadas) ? fixadas : [];

    if (ehGuiaFixada(atuais, typeId, itemId)) {
        return atuais.filter(
            (e) => !(e && e.typeId === typeId && String(e.itemId) === String(itemId))
        );
    }

    return [...atuais, { typeId, itemId }];
};

/**
 * As fixadas primeiro, preservando a ordem DENTRO de cada grupo.
 *
 * Ordenação estável, e isso é o ponto: a ordem que a pessoa arrastou continua
 * valendo entre as fixadas e entre as não fixadas. Fixar reposiciona a guia,
 * não embaralha a barra.
 *
 * `identificar` recebe o item e devolve `{ typeId, itemId }` -- quem sabe
 * extrair isso é quem monta a lista, porque cada tipo de item (página,
 * chamado, conversa) guarda o id num campo diferente.
 */
export const comFixadasPrimeiro = (itens, fixadas, identificar) => {
    const lista = Array.isArray(itens) ? itens : [];
    const marcadas = new Set(
        (Array.isArray(fixadas) ? fixadas : []).map((e) => identidade(e.typeId, String(e.itemId)))
    );

    const fixada = (item) => {
        const { typeId, itemId } = identificar(item) || {};

        return marcadas.has(identidade(typeId, String(itemId)));
    };

    /*
     * Dois baldes em vez de `sort`: o `Array.prototype.sort` só é garantido
     * estável desde o ES2019, e um navegador antigo embaralharia a ordem que
     * a pessoa arrastou -- um defeito que só apareceria na máquina dela.
     */
    const primeiro = [];
    const depois = [];

    lista.forEach((item) => (fixada(item) ? primeiro : depois).push(item));

    return [...primeiro, ...depois];
};
